import {
  Component, OnInit, OnDestroy,
  inject, signal, computed, effect,
  ViewChild, TemplateRef,
} from '@angular/core';
import { CommonModule }           from '@angular/common';
import { FormsModule }            from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { toSignal }               from '@angular/core/rxjs-interop';
import { map }                    from 'rxjs/operators';
import { TreeNodeEvent }          from '@libs/front/components/tree-view/tree-view.component';

import { NavLeftService }        from 'src/app/services/nav-left.service';
import { PageBarService }        from 'src/app/services/page-bar.service';
import { SiteNavigationService } from 'src/app/services/new_services/site-navigation.service';
import { ButtonComponent }       from '@libs/front/components/button/button.component';
import { InputComponent }        from '@libs/front/components/input/input.component';
import { SelectComponent, SelectOption } from '@libs/front/components/select/select.component';
import { CartePopupComponent }    from '@libs/front/components/popup-panel/popup-panel.component';
import { SiteDashboardComponent } from 'src/app/features/site/dashboard/site-dashboard.component';

import {
  TreeGroupingConfig,
  GroupingLevel,
  GROUPING_OPTIONS_L1,
  GROUPING_OPTIONS_L2,
  GROUPING_OPTIONS_L3,
  GROUPING_LEVEL_LABELS,
} from 'src/app/shared/models/tree-grouping.types';

// import { ProjectDashboardComponent }   from 'src/app/features/project/project-dashboard/project-dashboard.component';
// import { ProjectFilesComponent }       from 'src/app/features/project/project-files/project-files.component';
// import { NodeDashboardComponent }      from 'src/app/features/node/node-dashboard/node-dashboard.component';
// import { BlastDashboardComponent }     from 'src/app/features/blast/blast-dashboard/blast-dashboard.component';
// import { BlastFilesComponent }         from 'src/app/features/blast/blast-files/blast-files.component';
// import { BlastKpiComponent }           from 'src/app/features/blast/blast-kpi/blast-kpi.component';
// import { BlastDesignComponent }        from 'src/app/features/blast/blast-design/blast-design.component';
// import { BlastLoadingComponent }       from 'src/app/features/blast/blast-loading/blast-loading.component';
// import { BlastFragmentationComponent } from 'src/app/features/blast/blast-fragmentation/blast-fragmentation.component';
// import { BlastVibrationComponent }     from 'src/app/features/blast/blast-vibration/blast-vibration.component';
// import { BlastEnvironmentComponent }   from 'src/app/features/blast/blast-environment/blast-environment.component';
// import { BlastFeedbackComponent }      from 'src/app/features/blast/blast-feedback/blast-feedback.component';

// ── Tabs ──────────────────────────────────────────────────────────────────────

const SITE_TABS = [
  { id: 'maps',              label: 'Maps',              dataAttr: 'site-tab-site-maps'              },
  { id: 'dashboard',         label: 'Dashboard',         dataAttr: 'site-tab-site-dashboard'         },
  { id: 'files',             label: 'Files',             dataAttr: 'site-tab-site-files'             },
  { id: 'measurement-point', label: 'Measurement point', dataAttr: 'site-tab-site-measurement-point' },
];

const PROJECT_TABS = [
  { id: 'maps',      label: 'Maps',      dataAttr: 'site-tab-project-maps'      },
  { id: 'dashboard', label: 'Dashboard', dataAttr: 'site-tab-project-dashboard' },
  { id: 'files',     label: 'Files',     dataAttr: 'site-tab-project-files'     },
];

const NODE_TABS = [
  { id: 'dashboard', label: 'Dashboard', dataAttr: 'site-tab-node-dashboard' },
];

const BLAST_TABS = [
  { id: 'maps',          label: 'Maps',          dataAttr: 'site-tab-blast-maps'          },
  { id: 'dashboard',     label: 'Dashboard',     dataAttr: 'site-tab-blast-dashboard'     },
  { id: 'files',         label: 'Files',         dataAttr: 'site-tab-blast-files'         },
  { id: 'kpi',           label: 'KPI',           dataAttr: 'site-tab-blast-kpi'           },
  { id: 'design',        label: 'Design',        dataAttr: 'site-tab-blast-design'        },
  { id: 'loading',       label: 'Loading',       dataAttr: 'site-tab-blast-loading'       },
  { id: 'fragmentation', label: 'Fragmentation', dataAttr: 'site-tab-blast-fragmentation' },
  { id: 'vibration',     label: 'Vibration',     dataAttr: 'site-tab-blast-vibration'     },
  { id: 'environment',   label: 'Environment',   dataAttr: 'site-tab-blast-environment'   },
  { id: 'feedback',      label: 'Feedback',      dataAttr: 'site-tab-blast-feedback'      },
];

function toOptions(levels: GroupingLevel[]): SelectOption[] {
  return levels.map(l => ({ id: l, label: GROUPING_LEVEL_LABELS[l] }));
}

// ── Composant ─────────────────────────────────────────────────────────────────

@Component({
  selector:    'app-new-site',
  standalone:  true,
  templateUrl: './new-site.component.html',
  styleUrls:   ['./new-site.component.scss'],
  imports: [
    CommonModule, FormsModule,
    ButtonComponent, InputComponent, SelectComponent, CartePopupComponent,
    SiteDashboardComponent,
    // ProjectDashboardComponent, ProjectFilesComponent,
    // NodeDashboardComponent,
    // BlastDashboardComponent, BlastFilesComponent,
    // BlastKpiComponent, BlastDesignComponent,
    // BlastLoadingComponent, BlastFragmentationComponent,
    // BlastVibrationComponent, BlastEnvironmentComponent,
    // BlastFeedbackComponent,
  ],
})
export class NewSiteComponent implements OnInit, OnDestroy {

  private readonly route   = inject(ActivatedRoute);
  readonly         router  = inject(Router);
  readonly         navLeft = inject(NavLeftService);
  readonly         pageBar = inject(PageBarService);
  readonly         siteNav = inject(SiteNavigationService);

  // ── ViewChild — résolus avant ngOnInit (static: true) ────────────────────
  @ViewChild('headerTpl',         { static: true }) private headerTpl!:         TemplateRef<any>;
  @ViewChild('searchTpl',         { static: true }) private searchTpl!:         TemplateRef<any>;
  @ViewChild('siteCardTpl',       { static: true }) private siteCardTpl!:       TemplateRef<any>;
  @ViewChild('pageBarSiteTpl',    { static: true }) private pageBarSiteTpl!:    TemplateRef<any>;
  @ViewChild('pageBarProjectTpl', { static: true }) private pageBarProjectTpl!: TemplateRef<any>;
  @ViewChild('pageBarNodeTpl',    { static: true }) private pageBarNodeTpl!:    TemplateRef<any>;
  @ViewChild('pageBarBlastTpl',   { static: true }) private pageBarBlastTpl!:   TemplateRef<any>;

  // ── QueryParams ───────────────────────────────────────────────────────────

  readonly queryParams = toSignal(
    this.route.queryParamMap.pipe(
      map(p => ({
        projectId: p.get('projectId') ? Number(p.get('projectId')) : null,
        blastId:   p.get('blastId')   ? Number(p.get('blastId'))   : null,
        nodeType:  p.get('nodeType')  ?? null,
        nodeValue: p.get('nodeValue') ?? null,
        tab:       p.get('tab')       ?? null,
      }))
    ),
    { initialValue: { projectId: null, blastId: null, nodeType: null, nodeValue: null, tab: null } }
  );

  // ── Computed ──────────────────────────────────────────────────────────────

  readonly activeScreen = computed(() => {
    const p = this.queryParams();
    if (p.blastId)                return 'blast'   as const;
    if (p.nodeType === 'project') return 'project' as const;
    if (p.nodeType)               return 'node'    as const;
    if (p.projectId)              return 'project' as const;
    return 'site' as const;
  });

  readonly activeTabs = computed(() => {
    switch (this.activeScreen()) {
      case 'blast':   return BLAST_TABS;
      case 'project': return PROJECT_TABS;
      case 'node':    return NODE_TABS;
      default:        return SITE_TABS;
    }
  });

  readonly pageBarLabel = computed(() => {
    const p = this.queryParams();
    switch (this.activeScreen()) {
      case 'blast':   return `Blast ${p.blastId}`;
      case 'project': return p.projectId ? `Project ${p.projectId}` : (p.nodeValue ?? '');
      case 'node':    return `${p.nodeValue}`;
      default:        return this.siteNav.siteName();
    }
  });

  // ── Filter popup ──────────────────────────────────────────────────────────

  readonly filterOpen     = signal(false);
  readonly filterPopupPos = signal({ top: '0px', right: '0px' });

  openFilter(el: HTMLElement): void {
    const rect = el.getBoundingClientRect();
    this.filterPopupPos.set({
      top:   `${rect.bottom + 4}px`,
      right: `${window.innerWidth - rect.right}px`,
    });
    this.filterOpen.set(!this.filterOpen());
  }

  applyGrouping(): void {
    this.confirmSettings();
    this.filterOpen.set(false);
  }

  // ── Settings ──────────────────────────────────────────────────────────────

  readonly pendingL1 = signal<GroupingLevel>(this.siteNav.grouping().level1);
  readonly pendingL2 = signal<GroupingLevel>(this.siteNav.grouping().level2);
  readonly pendingL3 = signal<GroupingLevel>(this.siteNav.grouping().level3);

  readonly optionsL1 = computed(() => toOptions(GROUPING_OPTIONS_L1));
  readonly optionsL2 = computed(() =>
    toOptions(GROUPING_OPTIONS_L2.filter(l => l === 'none' || l !== this.pendingL1()))
  );
  readonly optionsL3 = computed(() => {
    if (this.pendingL2() === 'none') return [];
    return toOptions(
      GROUPING_OPTIONS_L3.filter(
        l => l === 'none' || (l !== this.pendingL1() && l !== this.pendingL2())
      )
    );
  });
  readonly level3Disabled = computed(() => this.pendingL2() === 'none');

  // ── Constructeur — uniquement les effects sans ViewChild ──────────────────

  constructor() {
    const siteId = Number(this.route.snapshot.paramMap.get('siteId'));
    if (!isNaN(siteId) && siteId > 0) {
      this.siteNav.setSite(siteId);
    }

    // Synchronise queryParams → selectedNodeId (pas de ViewChild)
    effect(() => {
      this.siteNav.syncFromQueryParams(this.queryParams());
    });

    // Met à jour treeData + selectedNodeId dans NavLeftService
    // Ces deux signals viennent du service, pas des ViewChild → OK dans le constructeur
    effect(() => {
      this.navLeft.setTreeData(this.siteNav.treeData());
    });

    effect(() => {
      this.navLeft.setSelectedNodeId(this.siteNav.selectedNodeId());
    });

    // Met à jour le label de la page-bar quand le nom du site arrive
    effect(() => {
      this.pageBar.setLabel(this.pageBarLabel());
    });

    // Met à jour les boutons d'actions quand l'écran change
    effect(() => {
      this.pageBar.setActionsTemplate(this._getActionsTemplate());
    });

    // Sync tabs + tab actif depuis l'URL
    effect(() => {
      const tabs   = this.activeTabs();
      const urlTab = this.queryParams().tab;
      const tabId  = urlTab && tabs.some(t => t.id === urlTab) ? urlTab : tabs[0]?.id ?? '';
      this.pageBar.setTabs(tabs);
      this.pageBar.setActiveTab(tabId);
    });
  }

  // ── ngOnInit — config statique avec ViewChild (garantis résolus ici) ──────

  ngOnInit(): void {
    // Config statique nav-left : templates + callbacks uniquement (treeData géré par effect)
    this.navLeft.set({
      treeData:         [],
      headerTemplate:   this.headerTpl,
      searchTemplate:   this.searchTpl,
      siteCardTemplate: this.siteCardTpl,
      onNodeClick:      (e) => this.onNodeClick(e),
    });

    // Config statique page-bar : templates d'actions + callbacks
    this.pageBar.set({
      label:           this.pageBarLabel(),
      backLabel:       'Area and sites',
      tabs:            this.activeTabs(),
      activeTabId:     this.activeTabs()[0].id,
      actionsTemplate: this._getActionsTemplate(),
      onTabClick:      (tab) => this.router.navigate([], {
        relativeTo:          this.route,
        queryParams:         { tab: tab.id },
        queryParamsHandling: 'merge',
      }),
      onBack:          () => this.router.navigate(['/area']),
    });
  }

  ngOnDestroy(): void {
    this.navLeft.reset();
    this.pageBar.reset();
  }

  // ── Handlers settings ─────────────────────────────────────────────────────

  onL1Change(option: SelectOption | null): void {
    if (!option) return;
    const val = option.id as GroupingLevel;
    this.pendingL1.set(val);
    if (this.pendingL2() === val) this.pendingL2.set('none');
    if (this.pendingL3() === val) this.pendingL3.set('none');
  }

  onL2Change(option: SelectOption | null): void {
    const val = (option?.id ?? 'none') as GroupingLevel;
    this.pendingL2.set(val);
    if (val === 'none') {
      this.pendingL3.set('none');
    } else if (this.pendingL3() === val) {
      this.pendingL3.set('none');
    }
  }

  onL3Change(option: SelectOption | null): void {
    this.pendingL3.set((option?.id ?? 'none') as GroupingLevel);
  }

  confirmSettings(): void {
    const config: TreeGroupingConfig = {
      level1: this.pendingL1() as Exclude<GroupingLevel, 'none'>,
      level2: this.pendingL2(),
      level3: this.pendingL3(),
    };
    this.siteNav.setGrouping(config);
  }

  onNodeClick(event: TreeNodeEvent): void {
    this.siteNav.navigateFromNodeClick(event, this._firstTabForNodeType(event.type));
  }

  onSiteCardClick(): void {
    const siteId = this.siteNav.siteId();
    if (!siteId) return;
    this.router.navigate(['/new-site', siteId], {
      queryParams: { tab: SITE_TABS[0].id },
    });
  }

  private _firstTabForNodeType(type: string): string {
    switch (type) {
      case 'blast':   return BLAST_TABS[0]!.id;
      case 'project': return PROJECT_TABS[0]!.id;
      default:        return NODE_TABS[0]!.id;
    }
  }

  private _getActionsTemplate(): TemplateRef<any> {
    switch (this.activeScreen()) {
      case 'blast':   return this.pageBarBlastTpl;
      case 'project': return this.pageBarProjectTpl;
      case 'node':    return this.pageBarNodeTpl;
      default:        return this.pageBarSiteTpl;
    }
  }
}